import { zodResolver } from "@hookform/resolvers/zod";
import { Grid } from "@mui/material";
import { useRef } from "react";
import { type UseFormReturn, useForm } from "react-hook-form";

import { ProfilePictureUpload } from "Actors/Components/ProfilePictureUpload";
import {
  type ActorFormData,
  actorFormSchema,
} from "Actors/Forms/actorFormSchema";
import { BlFormCheckbox } from "Forms/Components/BlFormCheckbox";
import { BlFormDatePicker } from "Forms/Components/BlFormDatePicker";
import { BlFormDropdown } from "Forms/Components/BlFormDropdown";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { BlPhoneInput } from "Forms/Components/BlPhoneInput";
import { useDefaultValues } from "Forms/Hooks/useDefaultValues";
import useHandleServerError from "Forms/Hooks/useHandleServerError";
import { ibanMaskProps, swiftMaskProps } from "Forms/Utils/bankingMasks";
import {
  getCountryOptions,
  getGenderOptions,
  getIndustryOptions,
  getLanguageOptions,
} from "Forms/Utils/EnumOptions";
import { getPostalCodeMaskRegex } from "Forms/Utils/postalCodeUtils";
import type {
  ActorDetailDto,
  CountryCodes,
  GenderType,
  LanguageCodes,
} from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";

export interface ActorFormHandle {
  submit: (
    onValid: (data: ActorFormData) => void | Promise<void>,
  ) => () => void;
  reset: () => void;
}

interface ActorFormProps {
  actor?: ActorDetailDto | null;
  formRef: React.RefObject<ActorFormHandle | null>;
  pendingPicture?: File | null;
  onPendingPictureChange?: (file: File | null) => void;
  serverError?: Error | null;
}

export const ActorForm = ({
  actor,
  formRef,
  pendingPicture,
  onPendingPictureChange,
  serverError,
}: ActorFormProps): React.ReactNode => {
  const { t } = useResource();

  const internalFormRef = useRef<UseFormReturn<ActorFormData> | null>(null);

  const defaultValues = useDefaultValues(
    () => buildDefaults(actor),
    [actor],
    internalFormRef,
  );

  const form = useForm<ActorFormData>({
    resolver: zodResolver(actorFormSchema),
    defaultValues,
  });

  internalFormRef.current = form;

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = form;

  useHandleServerError({
    form,
    error: serverError,
    formKey: "firstName",
    resource: Resources.ApiErrors.SaveActor,
  });

  formRef.current = {
    submit: (onValid) => () => handleSubmit(onValid)(),
    reset: () => reset(),
  };

  const hasCustomBillingDetails = watch("hasCustomBillingDetails");
  const watchedCountry = watch("country");
  const watchedBillingCountry = watch("billingCountry");

  const isEditMode = !!actor;

  return (
    <Grid container spacing={Spacing.M}>
      <Grid size={12}>
        <ProfilePictureUpload
          actorID={isEditMode ? actor.actorID : undefined}
          userID={isEditMode ? actor.userID : undefined}
          profilePictureUrl={actor?.profilePicture}
          firstName={actor?.firstName ?? ""}
          lastName={actor?.lastName ?? ""}
          pendingFile={pendingPicture}
          onPendingFileChange={onPendingPictureChange}
        />
      </Grid>

      <Grid size={6}>
        <BlFormInput
          control={control}
          name="firstName"
          label={t(Resources.Actors.CreateActorModal.FirstName)}
          errors={errors}
          placeholder={t(
            Resources.Actors.CreateActorModal.FirstNamePlaceholder,
          )}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormInput
          control={control}
          name="lastName"
          label={t(Resources.Actors.CreateActorModal.LastName)}
          errors={errors}
          placeholder={t(Resources.Actors.CreateActorModal.LastNamePlaceholder)}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormInput
          control={control}
          name="email"
          label={t(Resources.Actors.CreateActorModal.Email)}
          errors={errors}
          placeholder={t(Resources.Actors.CreateActorModal.EmailPlaceholder)}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlPhoneInput
          control={control}
          name="phone"
          errors={errors}
          label={t(Resources.Actors.CreateActorModal.Phone)}
          placeholder={t(Resources.Actors.CreateActorModal.PhonePlaceholder)}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormDatePicker
          control={control}
          name="birthDate"
          label={t(Resources.Actors.CreateActorModal.DateOfBirth)}
          errors={errors}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormDropdown
          control={control}
          name="gender"
          label={t(Resources.Actors.CreateActorModal.Gender)}
          options={getGenderOptions()}
          errors={errors}
          placeholder={t(Resources.Actors.CreateActorModal.GenderPlaceholder)}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormDropdown
          control={control}
          name="language"
          label={t(Resources.Actors.CreateActorModal.Language)}
          options={getLanguageOptions()}
          errors={errors}
          placeholder={t(Resources.Actors.CreateActorModal.LanguagePlaceholder)}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormDropdown
          control={control}
          name="industries"
          label={t(Resources.Actors.CreateActorModal.Industry)}
          options={getIndustryOptions()}
          errors={errors}
          placeholder={t(Resources.Actors.CreateActorModal.IndustryPlaceholder)}
          multiple
        />
      </Grid>
      <Grid size={6}>
        <BlFormDropdown
          control={control}
          name="country"
          label={t(Resources.Actors.CreateActorModal.Country)}
          options={getCountryOptions()}
          errors={errors}
          placeholder={t(Resources.Actors.CreateActorModal.CountryPlaceholder)}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormInput
          control={control}
          name="address"
          label={t(Resources.Actors.CreateActorModal.Address)}
          errors={errors}
          placeholder={t(Resources.Actors.CreateActorModal.AddressPlaceholder)}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormInput
          control={control}
          name="city"
          label={t(Resources.Actors.CreateActorModal.City)}
          errors={errors}
          placeholder={t(Resources.Actors.CreateActorModal.CityPlaceholder)}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormInput
          control={control}
          name="postalCode"
          label={t(Resources.Actors.CreateActorModal.PostalCode)}
          errors={errors}
          placeholder={t(
            Resources.Actors.CreateActorModal.PostalCodePlaceholder,
          )}
          maskProps={{ mask: getPostalCodeMaskRegex(watchedCountry) }}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormInput
          control={control}
          name="iban"
          label={t(Resources.Actors.CreateActorModal.IBAN)}
          errors={errors}
          placeholder={t(Resources.Actors.CreateActorModal.IBANPlaceholder)}
          required
          maskProps={ibanMaskProps}
        />
      </Grid>
      <Grid size={6}>
        <BlFormInput
          control={control}
          name="swift"
          label={t(Resources.Actors.CreateActorModal.SWIFT)}
          errors={errors}
          placeholder={t(Resources.Actors.CreateActorModal.SWIFTPlaceholder)}
          maskProps={swiftMaskProps}
          required
        />
      </Grid>

      <Grid size={12}>
        <BlFormCheckbox
          control={control}
          name="hasCustomBillingDetails"
          label={t(Resources.Actors.CreateActorModal.Invoicing)}
          errors={errors}
          color="default"
        />
      </Grid>

      {hasCustomBillingDetails && (
        <>
          <Grid size={12}>
            <BlFormInput
              control={control}
              name="billingCompanyName"
              label={t(Resources.Actors.CreateActorModal.BusinessName)}
              errors={errors}
              placeholder={t(
                Resources.Actors.CreateActorModal.BusinessNamePlaceholder,
              )}
              required
            />
          </Grid>
          <Grid size={6}>
            <BlFormInput
              control={control}
              name="billingCompanyNumber"
              label={t(Resources.Actors.CreateActorModal.CompanyNumber)}
              errors={errors}
              placeholder={t(
                Resources.Actors.CreateActorModal.CompanyNumberPlaceholder,
              )}
              required
            />
          </Grid>
          <Grid size={6}>
            <BlFormInput
              control={control}
              name="billingTaxNumber"
              label={t(Resources.Actors.CreateActorModal.TaxNumber)}
              errors={errors}
              placeholder={t(
                Resources.Actors.CreateActorModal.TaxNumberPlaceholder,
              )}
              required
            />
          </Grid>
          <Grid size={6}>
            <BlFormDropdown
              control={control}
              name="billingCountry"
              label={t(Resources.Actors.CreateActorModal.BillingCountry)}
              options={getCountryOptions()}
              errors={errors}
              placeholder={t(
                Resources.Actors.CreateActorModal.BillingCountryPlaceholder,
              )}
              required
            />
          </Grid>
          <Grid size={6}>
            <BlFormInput
              control={control}
              name="billingAddress"
              label={t(Resources.Actors.CreateActorModal.BillingAddress)}
              errors={errors}
              placeholder={t(
                Resources.Actors.CreateActorModal.BillingAddressPlaceholder,
              )}
              required
            />
          </Grid>
          <Grid size={6}>
            <BlFormInput
              control={control}
              name="billingCity"
              label={t(Resources.Actors.CreateActorModal.BillingCity)}
              errors={errors}
              placeholder={t(
                Resources.Actors.CreateActorModal.BillingCityPlaceholder,
              )}
              required
            />
          </Grid>
          <Grid size={6}>
            <BlFormInput
              control={control}
              name="billingPostalCode"
              label={t(Resources.Actors.CreateActorModal.BillingPostalCode)}
              errors={errors}
              placeholder={t(
                Resources.Actors.CreateActorModal.BillingPostalCodePlaceholder,
              )}
              maskProps={{
                mask: getPostalCodeMaskRegex(
                  watchedBillingCountry ?? undefined,
                ),
              }}
              required
            />
          </Grid>
          <Grid size={6}>
            <BlFormInput
              control={control}
              name="billingIban"
              label={t(Resources.Actors.CreateActorModal.BillingIBAN)}
              errors={errors}
              placeholder={t(
                Resources.Actors.CreateActorModal.BillingIBANPlaceholder,
              )}
              required
              maskProps={ibanMaskProps}
            />
          </Grid>
          <Grid size={6}>
            <BlFormInput
              control={control}
              name="billingSwift"
              label={t(Resources.Actors.CreateActorModal.BillingSWIFT)}
              errors={errors}
              placeholder={t(
                Resources.Actors.CreateActorModal.BillingSWIFTPlaceholder,
              )}
              maskProps={swiftMaskProps}
              required
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

function buildDefaults(actor?: ActorDetailDto | null): ActorFormData {
  if (!actor) {
    return {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      birthDate: new Date(),
      gender: "" as GenderType,
      language: "" as LanguageCodes,
      industries: [],
      address: "",
      city: "",
      postalCode: "",
      country: "" as CountryCodes,
      iban: "",
      swift: "",
      hasCustomBillingDetails: false,
      billingCompanyName: "",
      billingCompanyNumber: "",
      billingTaxNumber: "",
      billingAddress: "",
      billingCity: "",
      billingPostalCode: "",
      billingCountry: null,
      billingIban: "",
      billingSwift: "",
    };
  }

  return {
    firstName: actor.firstName,
    lastName: actor.lastName,
    email: actor.email,
    phone: actor.phone ?? "",
    birthDate: actor.birthDate ? new Date(actor.birthDate) : new Date(),
    gender: actor.gender ?? ("" as GenderType),
    language: actor.language ?? ("" as LanguageCodes),
    industries: actor.industries ?? [],
    address: actor.address?.street ?? "",
    city: actor.address?.municipality ?? "",
    postalCode: actor.address?.postalCode ?? "",
    country: actor.address?.countryCode ?? ("" as CountryCodes),
    iban: actor.iban ?? "",
    swift: actor.swift ?? "",
    hasCustomBillingDetails: actor.hasCustomBillingDetails,
    billingCompanyName: actor.billingDetails?.companyName ?? "",
    billingCompanyNumber: actor.billingDetails?.companyNumber ?? "",
    billingTaxNumber: actor.billingDetails?.taxNumber ?? "",
    billingAddress: actor.billingDetails?.address?.street ?? "",
    billingCity: actor.billingDetails?.address?.municipality ?? "",
    billingPostalCode: actor.billingDetails?.address?.postalCode ?? "",
    billingCountry: actor.billingDetails?.address?.countryCode ?? null,
    billingIban: actor.billingDetails?.iban ?? "",
    billingSwift: actor.billingDetails?.swift ?? "",
  };
}
